import React, { useContext, useEffect, useState } from "react";
import { DatePicker } from "react-datepicker";
import ButtonInput from "./ButtonInput";
import { Typography } from "@mui/material";
import * as AppConfig from "@/config/AppConfig";
import { useTextDomainContext } from "@/app/providers";
import { func, string, object, bool } from "prop-types";

/**
 *
 * @param props
 */
type MultiDatePickerProps = {
  onValueChange?: (event: [Date | null, Date | null]) => void;
  componentLabel?: string;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  futureDates?: boolean;
  view?: string;
  selectedDate?: object;
  selectedYearDate?: object;
  getSelectedYearDate?: (date: Date) => void;
  [key: string]: unknown;
};

export const MultiDatePicker = (props: MultiDatePickerProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const [startDateValue, setStartDateValue] = useState(new Date());
  const [endDateValue, setEndDateValue] = useState(new Date());

  const dateValueChange = (event: [Date | null, Date | null] | null) => {
    const [start, end] = event ?? [];
    setStartDateValue(start);
    setEndDateValue(end);
    if (start && end && props.onValueChange) {
      props.onValueChange([start, end]);
    }
  };

  // Use useEffect with direct prop dependencies to avoid cascading renders
  useEffect(() => {
    if (props.defaultStartDate) {
      // Use setTimeout to defer state update and avoid cascading renders
      setTimeout(() => {
        setStartDateValue(props.defaultStartDate);
      }, 0);
    }
  }, [props.defaultStartDate]);

  useEffect(() => {
    if (props.defaultEndDate) {
      // Use setTimeout to defer state update and avoid cascading renders
      setTimeout(() => {
        setEndDateValue(props.defaultEndDate);
      }, 0);
    }
  }, [props.defaultEndDate]);

  // Create selectedOptions after all state updates
  const defaultOptions = {
    note: gettext("Select date range"),
    startDate: startDateValue,
    endDate: endDateValue,
    dateFormat: AppConfig.dateFormat,
  };

  const selectedOptions = {
    ...defaultOptions,
    ...props,
  };

  return (
    <DatePicker
      customInput={<ButtonInput {...props} />}
      selectsRange
      onChange={dateValueChange}
      {...selectedOptions}
    >
      <Typography sx={{ fontSize: "14px", color: "secondary.900" }}>
        {selectedOptions.note}
      </Typography>
    </DatePicker>
  );
};

MultiDatePicker.propTypes = {
  onValueChange: func,
  componentLabel: string,
  defaultStartDate: object,
  defaultEndDate: object,
  futureDates: bool,
  view: string,
  selectedDate: object,
  selectedYearDate: object,
  getSelectedYearDate: func,
};
